import React, { useState, useEffect } from 'react';

function UsersManager({ currentUser, workshops, onClose }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Модалки
  const [showForm, setShowForm] = useState(false);        // создать
  const [editingUser, setEditingUser] = useState(null);    // редактировать
  const [passwordUser, setPasswordUser] = useState(null);  // сменить пароль
  const [deletingUser, setDeletingUser] = useState(null);  // удалить

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Ошибка загрузки');
      setUsers(await res.json());
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const getRoleLabel = (role) => {
    if (role === 'director') return '👑 Директор';
    if (role === 'admin') return '🛡 Админ';
    if (role === 'hr') return '👔 Отдел кадров';
    if (role === 'workshop_manager') return '🏭 Начальник цеха';
    return role;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>👥 Пользователи системы</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="users-toolbar">
            <button
              className="btn btn-primary"
              onClick={() => setShowForm(true)}
            >
              ➕ Добавить пользователя
            </button>
          </div>

          {loading ? (
            <div className="empty" style={{ padding: '30px' }}>⏳ Загрузка...</div>
          ) : error ? (
            <div className="empty" style={{ padding: '30px', color: '#c62828' }}>{error}</div>
          ) : users.length === 0 ? (
            <div className="empty" style={{ padding: '30px' }}>Нет пользователей</div>
          ) : (
            <table className="table users-table">
              <thead>
                <tr>
                  <th>Логин</th>
                  <th>ФИО</th>
                  <th>Роль</th>
                  <th>Цех</th>
                  <th>Статус</th>
                  <th style={{ width: '130px' }}></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isSelf = u.id === currentUser.id;
                  return (
                    <tr key={u.id} className={isSelf ? 'self-row' : ''}>
                      <td><b>{u.login}</b>{isSelf && ' (вы)'}</td>
                      <td>{u.full_name}</td>
                      <td><span className="role-badge">{getRoleLabel(u.role)}</span></td>
                      <td>{u.workshop_name || '—'}</td>
                      <td>
                        {u.is_active ? (
                          <span className="status-active">✅ Активен</span>
                        ) : (
                          <span className="status-blocked">🚫 Заблокирован</span>
                        )}
                      </td>
                      <td>
                        <div className="users-actions">
                          <button
                            className="btn-icon"
                            title="Изменить"
                            onClick={() => setEditingUser(u)}
                          >
                            ✏️
                          </button>
                          <button
                            className="btn-icon"
                            title="Сменить пароль"
                            onClick={() => setPasswordUser(u)}
                          >
                            🔑
                          </button>
                          {!isSelf && (
                            <button
                              className="btn-icon danger"
                              title="Удалить"
                              onClick={() => setDeletingUser(u)}
                            >
                              🗑
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="modal-footer">
          <div className="modal-footer-right">
            <button className="btn-cancel" onClick={onClose}>Закрыть</button>
          </div>
        </div>
      </div>

      {/* МОДАЛКА СОЗДАНИЯ/РЕДАКТИРОВАНИЯ */}
      {(showForm || editingUser) && (
        <UserForm
          user={editingUser}
          workshops={workshops}
          currentUser={currentUser}
          onSave={() => {
            setShowForm(false);
            setEditingUser(null);
            fetchUsers();
          }}
          onClose={() => {
            setShowForm(false);
            setEditingUser(null);
          }}
        />
      )}

      {/* МОДАЛКА СМЕНЫ ПАРОЛЯ */}
      {passwordUser && (
        <PasswordForm
          user={passwordUser}
          onSave={() => {
            setPasswordUser(null);
          }}
          onClose={() => setPasswordUser(null)}
        />
      )}

      {/* МОДАЛКА УДАЛЕНИЯ */}
      {deletingUser && (
        <ConfirmDelete
          user={deletingUser}
          onConfirm={async () => {
            try {
              const token = localStorage.getItem('token');
              const res = await fetch(
                `/api/users/${deletingUser.id}`,
                {
                  method: 'DELETE',
                  headers: { Authorization: `Bearer ${token}` }
                }
              );
              const data = await res.json();
              if (!res.ok) throw new Error(data.error || 'Ошибка удаления');
              setDeletingUser(null);
              fetchUsers();
            } catch (err) {
              alert('Ошибка: ' + err.message);
            }
          }}
          onClose={() => setDeletingUser(null)}
        />
      )}
    </div>
  );
}

// ============================================
// ФОРМА СОЗДАНИЯ / РЕДАКТИРОВАНИЯ
// ============================================
function UserForm({ user, workshops, currentUser, onSave, onClose }) {
  const isEdit = !!user;
  const isSelf = isEdit && user.id === currentUser.id;

  const [form, setForm] = useState({
    login: user?.login || '',
    password: '',
    full_name: user?.full_name || '',
    role: user?.role || 'workshop_manager',
    workshop_id: user?.workshop_id || '',
    is_active: user?.is_active !== false
  });

  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.full_name.trim()) {
      setError('Введите ФИО');
      return;
    }

    if (!isEdit) {
      if (!form.login.trim()) {
        setError('Введите логин');
        return;
      }
      if (form.password.length < 6) {
        setError('Пароль минимум 6 символов');
        return;
      }
    }

    if (form.role === 'workshop_manager' && !form.workshop_id) {
      setError('Для начальника цеха укажите цех');
      return;
    }

    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      const url = isEdit
        ? `/api/users/${user.id}`
        : '/api/users';
      const method = isEdit ? 'PUT' : 'POST';

      const body = isEdit
        ? {
            full_name: form.full_name,
            role: form.role,
            workshop_id: form.role === 'workshop_manager' ? form.workshop_id : null,
            is_active: form.is_active
          }
        : {
            login: form.login,
            password: form.password,
            full_name: form.full_name,
            role: form.role,
            workshop_id: form.role === 'workshop_manager' ? form.workshop_id : null
          };

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Ошибка сохранения');

      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-small" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? '✏️ Изменить пользователя' : '➕ Новый пользователь'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          {!isEdit && (
            <>
              <div className="form-group">
                <label>Логин *</label>
                <input
                  type="text"
                  value={form.login}
                  onChange={(e) => setForm({ ...form, login: e.target.value })}
                  placeholder="ivanov"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Пароль * (мин. 6 символов)</label>
                <input
                  type="text"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••"
                />
              </div>
            </>
          )}

          <div className="form-group">
            <label>ФИО *</label>
            <input
              type="text"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              placeholder="Иванов Иван Иванович"
              autoFocus={isEdit}
            />
          </div>

          <div className="form-group">
            <label>Роль *</label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              disabled={isSelf}
            >
              <option value="director">👑 Директор</option>
              <option value="admin">🛡 Админ</option>
              <option value="hr">👔 Отдел кадров</option>
              <option value="workshop_manager">🏭 Начальник цеха</option>
            </select>
            {isSelf && (
              <small style={{ color: '#6c757d', fontSize: '11px', marginTop: '4px' }}>
                Нельзя менять свою роль
              </small>
            )}
          </div>

          {form.role === 'workshop_manager' && (
            <div className="form-group">
              <label>Цех *</label>
              <select
                value={form.workshop_id}
                onChange={(e) => setForm({ ...form, workshop_id: e.target.value })}
              >
                <option value="">— Выберите цех —</option>
                {workshops.map((w) => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            </div>
          )}

          {isEdit && !isSelf && (
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  style={{ marginRight: '8px' }}
                />
                Активен (разблокирован)
              </label>
            </div>
          )}

          {error && <div className="login-error">{error}</div>}

          <div className="modal-footer">
            <div className="modal-footer-right">
              <button type="button" className="btn-cancel" onClick={onClose}>
                Отмена
              </button>
              <button type="submit" className="btn-save" disabled={saving}>
                {saving ? '⏳ Сохранение...' : isEdit ? 'Сохранить' : 'Создать'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================
// ФОРМА СМЕНЫ ПАРОЛЯ
// ============================================
function PasswordForm({ user, onSave, onClose }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Пароль минимум 6 символов');
      return;
    }
    if (password !== confirm) {
      setError('Пароли не совпадают');
      return;
    }

    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `/api/users/${user.id}/password`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ password })
        }
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Ошибка');

      alert('✅ Пароль изменён');
      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-small" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🔑 Смена пароля — {user.full_name}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-group">
            <label>Новый пароль * (мин. 6 символов)</label>
            <input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              placeholder="••••••"
            />
          </div>

          <div className="form-group">
            <label>Повторите пароль *</label>
            <input
              type="text"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="••••••"
            />
          </div>

          {error && <div className="login-error">{error}</div>}

          <div className="modal-footer">
            <div className="modal-footer-right">
              <button type="button" className="btn-cancel" onClick={onClose}>
                Отмена
              </button>
              <button type="submit" className="btn-save" disabled={saving}>
                {saving ? '⏳ Сохранение...' : 'Сохранить'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================
// ПОДТВЕРЖДЕНИЕ УДАЛЕНИЯ
// ============================================
function ConfirmDelete({ user, onConfirm, onClose }) {
  const [saving, setSaving] = useState(false);

  const handleConfirm = async () => {
    setSaving(true);
    await onConfirm();
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-small" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🗑 Удаление пользователя</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <p style={{ fontSize: '14px', lineHeight: '1.6' }}>
            Удалить пользователя <b>«{user.full_name}»</b> (логин: <b>{user.login}</b>)?
          </p>
          <p style={{ fontSize: '13px', color: '#c62828', marginTop: '10px', fontWeight: 600 }}>
            ⚠️ Действие необратимо. Пользователь больше не сможет войти в систему.
          </p>
        </div>

        <div className="modal-footer">
          <div className="modal-footer-right">
            <button className="btn-cancel" onClick={onClose}>
              Отмена
            </button>
            <button
              className="btn-dismiss-confirm"
              onClick={handleConfirm}
              disabled={saving}
            >
              {saving ? '⏳ Удаление...' : 'Удалить'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UsersManager;